import React, { memo } from "react";
import { ScrollView } from "@/components/atoms/ScrollView";

import CategoryMetricsComparison from "@/features/compare-players/components/organisms/CategoryMetricsComparison";
import DropDownPlayer from "@/features/compare-players/components/organisms/DropDownPlayer";
import ListPlayerCompare from "@/features/compare-players/components/organisms/ListPlayerCompare";
import MetricsGrid from "@/features/compare-players/components/organisms/MetricsGrid";
import PlusMinusChartComparison from "@/features/compare-players/components/organisms/PlusMinusChartComparison";

import MetricExplanationModal from "@/components/organisms/MetricExplanationModal";
import SkeletonComparePlayer from "@/components/organisms/SkeletonComparePlayer";
import { Box } from "@/components/atoms/Box";
import { Divider } from "@/components/atoms/Divider";
import { Heading } from "@/components/atoms/Heading";
import { Stack } from "@/components/atoms/Stack";

import {
  PlayerDetailListPlayersByTeamIdsAndPositionIdsQuery,
  PlayerDetailPlayerMetaFragment,
  WatchlistPlayerFragment,
} from "@/generated/graphql";
import { ICompositeMetric } from "@/stores/compositeMetric";
import { PlayerCardData } from "@/types/playerCardTypes";
import { ifWeb } from "@/utils/platform";

/**
 * Props for DesktopSidebar sub-component
 */
interface DesktopSidebarProps {
  watchlistName?: string;
  watchList?: PlayerCardData[];
  watchlistLoading: boolean;
  myRoster?: PlayerCardData[];
  myRosterLoading: boolean;
  onSelectComparisonPlayer: (player: PlayerCardData) => void;
  onLoadMore: (value: string) => void;
}

/**
 * Desktop sidebar for player selection from watchlist and roster
 * @param root0 - Component props
 * @param root0.watchlistName - Name of the selected watchlist
 * @param root0.watchList - Array of players in watchlist
 * @param root0.watchlistLoading - Loading state for watchlist
 * @param root0.myRoster - Array of players from user's roster
 * @param root0.myRosterLoading - Loading state for roster
 * @param root0.onSelectComparisonPlayer - Callback when player is selected
 * @param root0.onLoadMore - Callback to load more players
 */
const DesktopSidebar = ({
  watchlistName,
  watchList,
  watchlistLoading,
  myRoster,
  myRosterLoading,
  onSelectComparisonPlayer,
  onLoadMore,
}: DesktopSidebarProps) => (
  <Stack className="w-1/4 border-r border-surface-muted">
    <Heading variant="title-sm" className="px-4 pt-4 text-left">
      Select Player
    </Heading>
    {!!watchList && (
      <Stack
        className={`px-3 ${watchList.length > 10 ? "flex-1" : "max-h-fit"}`}
      >
        <ListPlayerCompare
          data={watchList}
          title={watchlistName ?? ""}
          loading={watchlistLoading}
          keyList="watchlist"
          onSelectComparePlayer={onSelectComparisonPlayer}
          onLoadMore={onLoadMore}
          showsVertical={false}
        />
        <Divider className="border-b border-surface-muted" />
      </Stack>
    )}
    <Stack className="flex-1 p-4">
      <ListPlayerCompare
        data={myRoster}
        title="My Roster"
        loading={myRosterLoading}
        keyList="myroster"
        onSelectComparePlayer={onSelectComparisonPlayer}
        onLoadMore={onLoadMore}
        showsVertical={false}
      />
    </Stack>
  </Stack>
);

/**
 * Props for ComparisonPlayerHeader sub-component
 */
interface ComparisonPlayerHeaderProps {
  isSmall: boolean;
  playerSelect?: string;
  selectedComparisonPlayer: WatchlistPlayerFragment | null;
  myRoster?: PlayerCardData[];
  watchList?: PlayerCardData[];
  myRosterLoading: boolean;
  watchlistLoading: boolean;
  onSelectComparisonPlayer: (player: PlayerCardData) => void;
  onLoadMore: (value: string) => void;
}

/**
 * Header section showing comparison player name (desktop) or dropdown (mobile)
 * @param root0 - Component props
 * @param root0.isSmall - Whether the screen is mobile-sized
 * @param root0.playerSelect - Name of selected comparison player
 * @param root0.selectedComparisonPlayer - Currently selected comparison player
 * @param root0.myRoster - Array of players from user's roster
 * @param root0.watchList - Array of players in watchlist
 * @param root0.myRosterLoading - Loading state for roster
 * @param root0.watchlistLoading - Loading state for watchlist
 * @param root0.onSelectComparisonPlayer - Callback when player is selected
 * @param root0.onLoadMore - Callback to load more players
 */
const ComparisonPlayerHeader = ({
  isSmall,
  playerSelect,
  selectedComparisonPlayer,
  myRoster,
  watchList,
  myRosterLoading,
  watchlistLoading,
  onSelectComparisonPlayer,
  onLoadMore,
}: ComparisonPlayerHeaderProps) => {
  if (!isSmall) {
    return (
      <Heading variant="title-lg" className="shrink pr-10 text-left">
        {playerSelect ?? "--"}
      </Heading>
    );
  }

  return (
    <DropDownPlayer
      selectedComparisonPlayer={selectedComparisonPlayer}
      onSelectComparisonPlayer={onSelectComparisonPlayer}
      myRosterLoading={myRosterLoading}
      watchlistLoading={watchlistLoading}
      myRoster={myRoster}
      watchList={watchList}
      onLoadMore={onLoadMore}
    />
  );
};
/**
 *
 */
interface MainViewProps {
  player: PlayerDetailPlayerMetaFragment | null | undefined;
  loading: boolean;
  isSmall: boolean;
  playerSelect?: string;
  watchlistName?: string;
  myRosterLoading: boolean;
  watchlistLoading: boolean;
  myRoster?: PlayerCardData[];
  watchList?: PlayerCardData[];
  selectedComparisonPlayer: WatchlistPlayerFragment | null;
  /**
   * True while the selected player's full-detail query is in flight (SE-5377).
   * The selected node is picked instantly but its season arrays + heavy bioData
   * are re-fetched on select; the comparison charts gate on this so they don't
   * flash an empty selected-player series before the detail resolves.
   */
  selectedComparisonLoading: boolean;
  currentPlayerCompositeMetricsArray: ICompositeMetric[][] | null;
  selectedPlayerCompositeMetricsArray: ICompositeMetric[][] | null;
  onSelectComparisonPlayer: (player: PlayerCardData) => void;
  onLoadMore: (value: string) => void;
  explanationModalOpen: boolean;
  selectedMetricId: string | null;
  selectedMetricValue: number | string | null;
  selectedRosterValues: number[];
  listPlayerSameTeamAndPosition?: PlayerDetailListPlayersByTeamIdsAndPositionIdsQuery;
  /**
   * True while the roster peers backing the explanation modal's comparison
   * chart are expected but not yet in hand (SE-7481). Without it the chart
   * region's four-state machine (SE-7394) reads `undefined` and can never
   * resolve to `loading`, so in-flight data is reported as absent data.
   */
  isFetchingListPlayerSameTeamAndPosition: boolean;
  onCurrentPlayerMetricClick: (metric: ICompositeMetric) => void;
  onSelectedPlayerMetricClick: (metric: ICompositeMetric) => void;
  onCloseModal: () => void;
}

/**
 * Main view component for the Compare Players screen
 * Displays side-by-side player comparison with metrics, charts, and player selection
 * @param root0 - Component props
 * @param root0.player - The primary player being viewed and compared
 * @param root0.loading - Whether the player data is currently loading
 * @param root0.isSmall - Whether the screen is mobile-sized (affects layout)
 * @param root0.watchlistName - Name of the selected watchlist for player selection
 * @param root0.playerSelect - Name of the currently selected comparison player
 * @param root0.myRoster - Array of players from user's roster for selection
 * @param root0.watchList - Array of players from watchlist for selection
 * @param root0.myRosterLoading - Loading state for roster data
 * @param root0.watchlistLoading - Loading state for watchlist data
 * @param root0.selectedComparisonPlayer - The player currently selected for comparison
 * @param root0.selectedComparisonLoading - Whether the selected player's full-detail re-fetch is in flight
 * @param root0.currentPlayerCompositeMetricsArray - Composite metrics for primary player
 * @param root0.selectedPlayerCompositeMetricsArray - Composite metrics for comparison player
 * @param root0.onLoadMore - Callback to load more players when scrolling to end
 * @param root0.onSelectComparisonPlayer - Callback when a player is selected for comparison
 * @param root0.explanationModalOpen - Whether the metric explanation modal is open
 * @param root0.selectedMetricId - ID of the metric being explained in modal
 * @param root0.selectedMetricValue - Value of the metric being explained
 * @param root0.selectedRosterValues - Roster values for metric distribution in modal
 * @param root0.listPlayerSameTeamAndPosition - Players with same team and position for comparison
 * @param root0.isFetchingListPlayerSameTeamAndPosition - Whether those comparison players are still being fetched
 * @param root0.onCurrentPlayerMetricClick - Callback when clicking primary player's metric
 * @param root0.onSelectedPlayerMetricClick - Callback when clicking comparison player's metric
 * @param root0.onCloseModal - Callback to close the metric explanation modal
 */
const MainView = ({
  player,
  loading,
  isSmall,
  watchlistName,
  playerSelect,
  myRoster,
  watchList,
  myRosterLoading,
  watchlistLoading,
  selectedComparisonPlayer,
  selectedComparisonLoading,
  currentPlayerCompositeMetricsArray,
  selectedPlayerCompositeMetricsArray,
  onLoadMore,
  onSelectComparisonPlayer,
  explanationModalOpen,
  selectedMetricId,
  selectedMetricValue,
  selectedRosterValues,
  listPlayerSameTeamAndPosition,
  isFetchingListPlayerSameTeamAndPosition,
  onCurrentPlayerMetricClick,
  onSelectedPlayerMetricClick,
  onCloseModal,
}: MainViewProps) => (
  <Box className={isSmall ? "flex-1 px-4 pt-6" : "flex-1"}>
    <MetricExplanationModal
      isOpen={explanationModalOpen}
      onClose={onCloseModal}
      metricId={selectedMetricId}
      metricValue={selectedMetricValue}
      rosterValues={selectedRosterValues}
      player={player ?? undefined}
      listPlayerSameTeamAndPosition={listPlayerSameTeamAndPosition}
      isFetchingListPlayerSameTeamAndPosition={
        isFetchingListPlayerSameTeamAndPosition
      }
    />
    {loading ? (
      <SkeletonComparePlayer />
    ) : (
      <Stack direction="horizontal" className="gap-4">
        {!isSmall && (
          <DesktopSidebar
            watchlistName={watchlistName}
            watchList={watchList}
            watchlistLoading={watchlistLoading}
            myRoster={myRoster}
            myRosterLoading={myRosterLoading}
            onSelectComparisonPlayer={onSelectComparisonPlayer}
            onLoadMore={onLoadMore}
          />
        )}
        <Stack className={isSmall ? "flex-1" : "flex-1 py-6 pr-4"}>
          <Stack direction="horizontal" className="items-start gap-4 pb-4">
            <Heading
              variant={isSmall ? "display-sm" : "title-lg"}
              className="flex-1 text-left"
            >
              {player?.name ?? ""}
            </Heading>
            <Box className="flex-1 items-start">
              <ComparisonPlayerHeader
                isSmall={isSmall}
                playerSelect={playerSelect}
                selectedComparisonPlayer={selectedComparisonPlayer}
                myRoster={myRoster}
                watchList={watchList}
                myRosterLoading={myRosterLoading}
                watchlistLoading={watchlistLoading}
                onSelectComparisonPlayer={onSelectComparisonPlayer}
                onLoadMore={onLoadMore}
              />
            </Box>
          </Stack>
          <ScrollView
            className={ifWeb("flex-1", "")}
            keyboardShouldPersistTaps="handled"
            contentContainerClassName="flex-grow gap-4 pb-6"
            showsVerticalScrollIndicator={false}
          >
            <Stack direction="horizontal" className="flex-1 gap-4">
              <MetricsGrid
                shouldShowColorTransferMetric={false}
                metrics={currentPlayerCompositeMetricsArray}
                onMetricClick={onCurrentPlayerMetricClick}
              />
              <MetricsGrid
                shouldShowColorTransferMetric={false}
                metrics={selectedPlayerCompositeMetricsArray}
                onMetricClick={onSelectedPlayerMetricClick}
              />
            </Stack>
            <PlusMinusChartComparison
              currentPlayer={player}
              selectedPlayer={
                selectedComparisonLoading ? null : selectedComparisonPlayer
              }
            />
            <CategoryMetricsComparison
              currentPlayer={player}
              selectedPlayer={
                selectedComparisonLoading ? null : selectedComparisonPlayer
              }
            />
          </ScrollView>
        </Stack>
      </Stack>
    )}
  </Box>
);

MainView.displayName = "MainView";

export default memo(MainView);
